import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getBookingById } from "../../api/bookingsApi";
import PageLoader from "../../components/pageLoader";
import Toast from "../../components/toast";
import ToastContainer from "../../components/toastContainer";
import MainInfoTab from "./tabs/DetailTab";
import TravelTab from "./tabs/TravelTab";
import TechnicalTab from "./tabs/TechnicalTab";
import DocumentsTab from "./tabs/DocumentsTab";
import FinancialTab from "./tabs/FinancialTab";
import { ChevronLeftIcon } from "@heroicons/react/outline";

const TABS = [
  { key: "main_info", label: "Main Info" },
  { key: "financial", label: "Financial" },
  { key: "accommodation", label: "Travel & Accommodation" },
  { key: "technical", label: "Technical" },
  { key: "documents", label: "Documents" },
];

const BookingDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("main_info");
  const [toast, setToast] = useState(null);

  const fetchBooking = async () => {
    try {
      setLoading(true);
      const data = await getBookingById(id);
      setBooking(data);
    } catch (err) {
      setToast({
        type: "error",
        message:
          err?.response?.data?.detail || "Failed to load the booking info",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBooking();
  }, [id]);

  if (loading) return <PageLoader />;

  if (!booking) {
    return (
      <div className="flex items-center justify-center h-48 text-slate-400 text-sm">
        No booking info found.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* ── Header ── */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-white transition"
        >
          <ChevronLeftIcon className="w-4 h-4" />
          Back to Bookings
        </button>

        {/* Booking identity pill */}
        {booking.artist_name && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500">Booking</span>
            <span className="text-xs font-medium text-white bg-slate-800 border border-white/10 px-3 py-1 rounded-full">
              {booking.artist_name}
              {booking.date ? ` · ${booking.date}` : ""}
            </span>
          </div>
        )}
      </div>

      {/* ── Tabs ── */}
      <div className="border-b border-white/10">
        <div className="flex gap-1 overflow-x-auto pb-px">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`relative px-4 py-2.5 text-xs font-medium uppercase tracking-wider whitespace-nowrap transition rounded-t-lg
                ${
                  activeTab === tab.key
                    ? "text-pink-400 bg-pink-500/8"
                    : "text-slate-500 hover:text-slate-300 hover:bg-slate-800/50"
                }`}
            >
              {tab.label}
              {activeTab === tab.key && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-pink-500 rounded-full" />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* ── Tab content ── */}
      <div>
        {activeTab === "main_info" && <MainInfoTab booking={booking} />}
        {activeTab === "financial" && (
          <FinancialTab booking={booking} onRefresh={fetchBooking} />
        )}
        {activeTab === "accommodation" && <TravelTab booking={booking} />}
        {activeTab === "technical" && <TechnicalTab booking={booking} />}
        {activeTab === "documents" && <DocumentsTab booking={booking} />}
      </div>

      <ToastContainer>
        {toast && (
          <Toast
            type={toast.type}
            message={toast.message}
            onClose={() => setToast(null)}
          />
        )}
      </ToastContainer>
    </div>
  );
};

export default BookingDetailsPage;
